export enum AppEnv {
  Production = 'Production',
  // Production preview environment that is for internal production testing is only accessible by company VPN
  Preview = 'Preview',
  Staging = 'Staging',
  Dev = 'Dev',
}

export interface AppEnvConfigs {
  ogWebDomain: string
  cdcPublicDomain: string
  webDomain: string
  cdcWebDomain: string
  webStaticDomain: string
  marketingDomain: string
  cdcDomain: string
  cdcStaticDomain: string
  gtmId: string
  ga4Id: string
  segmentId: string
  backendHosts: Record<string, string>
  priceStreamDomain: string
  legacyPriceDomain: string
  payloadCmsDomain: string
}

const productionEnvConfigs: AppEnvConfigs = {
  ogWebDomain: 'og.com',
  cdcPublicDomain: 'crypto.com',
  priceStreamDomain: 'price-stream.crypto.com',
  legacyPriceDomain: 'crypto.com',

  webDomain: 'market.crypto.com',
  cdcWebDomain: 'web.crypto.com',
  webStaticDomain: 'cdc-market-assets.crypto.com',
  marketingDomain: 'crypto.com',
  cdcDomain: 'crypto.com',
  cdcStaticDomain: 'static.crypto.com',
  gtmId: 'GTM-WG9FXPH',
  ga4Id: 'G-PC0RNJG7RR',
  segmentId: 'XgozqWHnR6BRRc9BgJCTNz3PzDntyajA',
  payloadCmsDomain: 'payload.crypto.com',
  backendHosts: {
    app: 'app.mona.co',
    price: 'price-api.crypto.com',
    priceMember: 'price-member-api.crypto.com',
    priceInternal: 'price-api.app-prod.local',
    tokenMetaInternal: 'token-meta.app-prod.local',
    coinsaiSunshine: 'api-coinsai-sunshine.mona.co',
    cryptoCoinInfo: 'crypto-coin-info-api.crypto.com',
  },
}

const previewEnvConfigs: AppEnvConfigs = {
  ...productionEnvConfigs,
  ogWebDomain: 'web-preview.og.com',
  webDomain: 'web-preview.crypto.com',
  cdcWebDomain: 'web-preview.crypto.com',
  webStaticDomain: 'web-preview-static.crypto.com',
}

const stagingEnvConfigs: AppEnvConfigs = {
  ogWebDomain: 'ogsta-og-web.3ona.co',
  cdcPublicDomain: 'stag-qa-www.3ona.co',
  webDomain: 'asta-cdc-market.3ona.co',
  cdcWebDomain: 'cwsta-cdc-web.3ona.co',
  webStaticDomain: 'asta-cdc-market-static.3ona.co',
  priceStreamDomain: 'stag-token-price-ws.3ona.co',
  legacyPriceDomain: 'stag-tpp.3ona.co',
  payloadCmsDomain: 'payload.3ona.co',

  marketingDomain: 'stag-qa-www.3ona.co',
  cdcDomain: '3ona.co',
  cdcStaticDomain: 'asta-static.3ona.co',
  gtmId: 'GTM-MHS3XPPV',
  ga4Id: 'G-PC0RNJG7RR',
  segmentId: '7CFmzPLcFK3YtEjPHLR7NoB3pwBhvvnQ',
  backendHosts: {
    app: 'app.3ona.co',
    price: 'stag-token-price.3ona.co',
    priceMember: 'stag-token-price-member.3ona.co',
    priceInternal: 'price-api.app-stag.local',
    tokenMetaInternal: 'token-meta.app-stag.local',
    coinsaiSunshine: 'uat-api-coinsai-sunshine.3ona.co',
    cryptoCoinInfo: 'stag-crypto-coin-info-api.3ona.co',
  },
}

// Dev connects to BE staging due to unstable BE dev environment
const devStagingEnvConfigs: AppEnvConfigs = {
  ...stagingEnvConfigs,
  webDomain: 'adev-cdc-market.3ona.co',
  webStaticDomain: 'adev-cdc-market-static.3ona.co',
}

// const devEnvConfigs: AppEnvConfigs = {
//   ...stagingEnvConfigs,
//   webDomain: 'adev-cdc-market.3ona.co',
//   webStaticDomain: 'adev-cdc-market-static.3ona.co',
//   cdcWebDomain: 'cwdev-cdc-web.3ona.co',
//   priceStreamDomain: 'dev-token-price-ws.3ona.co',
//   legacyPriceDomain: 'dev-tpp.3ona.co',

//   backendHosts: {
//     app: 'app.3ona.co',
//     price: 'dev-token-price.3ona.co',
//     priceMember: 'dev-token-price-member.3ona.co',
//     priceInternal: 'price-api.app-dev.local',
//     tokenMetaInternal: 'token-meta.app-dev.local',
//   },
// }

const appEnvConfigsMap: { [key in AppEnv]: AppEnvConfigs } = {
  [AppEnv.Production]: productionEnvConfigs,
  [AppEnv.Preview]: previewEnvConfigs,
  [AppEnv.Staging]: stagingEnvConfigs,
  [AppEnv.Dev]: devStagingEnvConfigs,
}

export const currentAppEnv = process.env.NEXT_PUBLIC_APP_ENV

export const isProductionAppEnv = currentAppEnv === AppEnv.Production

export const isProductionLikeAppEnv =
  currentAppEnv === AppEnv.Production || currentAppEnv === AppEnv.Preview

export const currentAppEnvConfigs = appEnvConfigsMap[currentAppEnv] ?? productionEnvConfigs

export const accessingRemoteBFF = process.env.NEXT_PUBLIC_ACCESS_REMOTE_BFF === 'true'

export const APP_VERSION = isProductionLikeAppEnv
  ? process.env.NEXT_PUBLIC_GITHUB_REF_NAME
  : process.env.NEXT_PUBLIC_LAST_COMMIT_HASH?.slice(0, 7)

export const isLocalRuntime = process.env.LOCAL_RUNTIME === 'true'

export const isTanstackQueryDevtoolsEnabled = process.env.TANSTACK_QUERY_DEVTOOLS_ENABLED === 'true'

export const localDisableThirdPartyServices =
  process.env.LOCAL_DISABLE_THIRD_PARTY_SERVICES === 'true'
